import { useEffect } from "react";
import { UseFormSetValue } from "react-hook-form";
import { CONFIG_PAGES, HTTP_STATUSES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { useDepartments, usePermissionsList, useRole } from "@mibuildweb/hooks";
import { getModulesByAccessPermissions } from "./utils/getModulesByAccessPermissions";
import { CONFIG_PATHS } from "../../../../routes/config/constants";
import { IRoleFormData } from "../ConfigRoles.types";

export const useRoleDetailsViewModel = ({
  setValue,
}: {
  setValue: UseFormSetValue<IRoleFormData>;
}) => {
  const { navigate } = useRouter();
  const {
    getRole,
    createRole,
    updateRole,
    roleDetails,
    loading: isRoleLoading,
  } = useRole();
  const {
    getDepartments,
    departments,
    loading: isDepartmentsLoading,
  } = useDepartments();
  const {
    permissionsList,
    getPermissionsList,
    loading: isPermissionsLoading,
  } = usePermissionsList();

  const getRoleDetails = async (id: string) => {
    const response = await getRole(id);

    if (response?.status === HTTP_STATUSES.OK) {
      const { departmentId, description, name, permissionNames } =
        response.data;

      // reset every field except workPermissionNames since they are handled in a separate view model
      setValue("departmentId", departmentId);
      setValue("description", description);
      setValue("name", name);
      setValue("permissionNames", permissionNames);
      setValue("modules", getModulesByAccessPermissions(permissionNames));
    }
  };

  const handleCreateRole = async (data: IRoleFormData) => {
    const response = await createRole(data);
    if (response.status === HTTP_STATUSES.OK) {
      navigate(CONFIG_PATHS[CONFIG_PAGES.ROLES_HR]);
    }
  };

  const handleUpdateRole = async (data: IRoleFormData) => {
    const response = await updateRole({
      ...data,
      modifiedOn: roleDetails?.modifiedOn,
    });

    if (response?.status === HTTP_STATUSES.OK && data.id) {
      getRoleDetails(data.id);
    }
  };

  useEffect(() => {
    getPermissionsList();
    getDepartments();
  }, []);

  return {
    getRoleDetails,
    permissionsList,
    getPermissionsList,
    handleCreateRole,
    handleUpdateRole,
    roleDetails,
    departments,
    isLoading: isRoleLoading || isDepartmentsLoading || isPermissionsLoading,
  };
};
